import type { CSSProperties, ReactNode } from "react";

// ─── 共通スタイル ───
//
// 形の基準（色とは別。DashboardTab も同じ値）:
//   カード      … 余白 20px 24px・角丸 12px・カードどうしの間隔 16px
//   内側の枠    … 余白 14px 16px・角丸 10px・下の間隔 16px（カードの中のお知らせ・補足・選択肢など）
//   入力・ボタン … 角丸 8px

/**
 * 白いカードを背景（#faf9f7）から浮かせる影。
 * 背景と白のコントラスト比は 1.05 しかなく、枠線だけではカードの境目が分かりにくい。
 * 色の値は変えずに、ごく薄い影で区切る（塗りのカードには付けない）
 */
export const CARD_SHADOW = "0 1px 2px rgba(26, 26, 26, 0.05), 0 2px 8px rgba(26, 26, 26, 0.05)";

export const card: CSSProperties = {
  background: "white",
  border: "1px solid #e8e7e3",
  borderRadius: 12,
  padding: "20px 24px",
  marginBottom: 16,
  boxShadow: CARD_SHADOW,
};

export const labelStyle: CSSProperties = { fontSize: 13, color: "#6b6b68", marginBottom: 6, fontWeight: 500 };

export const inputStyle: CSSProperties = {
  width: "100%",
  padding: "10px 14px",
  fontSize: 15,
  border: "1px solid #e8e7e3",
  borderRadius: 8,
  outline: "none",
  background: "white",
  color: "#1a1a1a",
  boxSizing: "border-box",
};

export const selectStyle: CSSProperties = { ...inputStyle, appearance: "auto" as any };

export const btnPrimary: CSSProperties = {
  padding: "10px 24px",
  fontSize: 14,
  fontWeight: 600,
  border: "none",
  borderRadius: 8,
  background: "#534AB7",
  color: "white",
  cursor: "pointer",
};

export const btnSecondary: CSSProperties = {
  padding: "10px 24px",
  fontSize: 14,
  fontWeight: 500,
  border: "1px solid #e8e7e3",
  borderRadius: 8,
  background: "white",
  color: "#1a1a1a",
  cursor: "pointer",
};

/**
 * 一覧の行・カードの見出しに置く操作ボタン（中）。
 * ボタンの大きさは 2 種類に絞る: 画面の主操作（新規追加など）は btnPrimary、それ以外はこれ
 */
export const btnCompact: CSSProperties = {
  ...btnSecondary,
  fontSize: 13,
  padding: "7px 14px",
  lineHeight: "18px",
  whiteSpace: "nowrap",
};

// ─── コンポーネント ───

export function SectionTitle({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>{children}</div>;
}

export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f0efeb", fontSize: 15 }}>
      <span style={{ width: 200, color: "#6b6b68", flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

export function StepBadge({ num, done }: { num: number; done: boolean }) {
  return (
    <span style={{
      width: 26, height: 26, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: 13, fontWeight: 600, marginRight: 10, flexShrink: 0,
      background: done ? "#1D9E75" : "#f5f4f0", color: done ? "white" : "#6b6b68",
    }}>{done ? "\u2713" : num}</span>
  );
}

export function Badge({ bg, color, children }: { bg: string; color: string; children: ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 14px", borderRadius: 12, fontSize: 13, fontWeight: 500, background: bg, color }}>
      {children}
    </span>
  );
}
